import type { FitAreaInfo } from "../types";
import type { MeasurementLines } from "../pose-detect";

export type GranularFit = "good" | "tight" | "loose" | "a-bit-tight" | "a-bit-loose" | "too-tight" | "too-loose";
type PromptUnit = "in" | "cm" | "mm";

/** Parse a range string like "96-100" or "96–100 cm" into min/max numbers */
function parseRange(s: string): { min: number; max: number } {
  const ns = s
    .replace(/[^\d.\-–]/g, " ")
    .trim()
    .split(/[\s\-–]+/)
    .filter(Boolean)
    .map(Number)
    .filter((n) => !isNaN(n));
  return ns.length ? { min: Math.min(...ns), max: Math.max(...ns) } : { min: 0, max: 0 };
}

/** Parse the first number out of a value string like "104 cm" */
function parseNum(s: string): number {
  const n = parseFloat(s.replace(/[^\d.]/g, ""));
  return isNaN(n) ? 0 : n;
}

function normalizeUnit(unit?: string | null): PromptUnit | null {
  const u = (unit || "").toLowerCase();
  if (u === "in" || u === "inch" || u === "inches") return "in";
  if (u === "cm") return "cm";
  if (u === "mm") return "mm";
  return null;
}

function detectUnitFromText(text?: string | null): PromptUnit | null {
  const t = String(text || "");
  if (/\bmm\b/i.test(t)) return "mm";
  if (/\bcm\b/i.test(t)) return "cm";
  if (/\bin\b|inch|inches|["”]/i.test(t)) return "in";
  return null;
}

function convertValue(value: number, from: PromptUnit, to: PromptUnit): number {
  if (from === to) return value;
  const inMm = from === "mm" ? value : from === "cm" ? value * 10 : value * 25.4;
  if (to === "mm") return inMm;
  if (to === "cm") return inMm / 10;
  return inMm / 25.4;
}

function formatUnitNumber(value: number, unit: PromptUnit): string {
  const precision = unit === "mm" ? 0 : 1;
  const rounded = Number(value.toFixed(precision));
  return Number.isInteger(rounded) ? String(rounded) : String(rounded);
}

function convertNumberTextToUnit(text: string, from: PromptUnit, to: PromptUnit): string {
  if (from === to) return text;
  const converted = text
    .replace(/\s*(cm|mm|in|inch|inches)\b/ig, "")
    .replace(/(\d+(?:\.\d+)?)/g, (raw) => formatUnitNumber(convertValue(Number(raw), from, to), to))
    .trim();
  return converted ? `${converted} ${to}` : text;
}

function normalizeMeasurementValueText(measurement: string, value: string, targetUnit: PromptUnit): string {
  if (isUnitlessShoeSizeMeasurement(measurement)) return formatUserMeasurementValue(measurement, value);
  const sourceUnit = detectUnitFromText(value);
  if (sourceUnit) return convertNumberTextToUnit(value, sourceUnit, targetUnit);
  const trimmed = String(value || "").trim();
  return /\d/.test(trimmed) ? `${trimmed} ${targetUnit}` : trimmed;
}

function normalizeRangeText(range: string, targetUnit: PromptUnit, header?: string): string {
  const sourceUnit = detectUnitFromText(range) || detectUnitFromText(header);
  if (!sourceUnit) return String(range || "").trim();
  return convertNumberTextToUnit(range, sourceUnit, targetUnit);
}

function promptHeaderLabel(header: string): string {
  return String(header || "").replace(/\s*\((?:cm|mm|in|inch|inches)\)\s*/ig, "").trim();
}

function isUnitlessShoeSizeMeasurement(measurement: string): boolean {
  const normalized = measurement.toLowerCase().replace(/\s*\(.*?\)\s*/g, " ").trim();
  if (/foot\s*length|length\s*\(cm\)|cm\s*\/\s*jp|mondo|mondopoint/i.test(normalized)) return false;
  return /\bshoe\s*size\b|\b(us|uk|eu)\b/.test(normalized);
}

function formatUserMeasurementValue(measurement: string, value: string): string {
  if (!isUnitlessShoeSizeMeasurement(measurement)) return value;
  return value.replace(/\s*(cm|mm|in|inch|inches)\b/ig, "").trim();
}

function normalizePromptSizeLabel(size?: string | null): string {
  return String(size || "")
    .trim()
    .replace(/\s+[–—-]\s+(?:UK|US|EU|IT|FR|DE|ES|JP|CN|KR|AU|BR)\s+.+$/i, "")
    .replace(/\s*\((?:UK|US|EU|IT|FR|DE|ES|JP|CN|KR|AU|BR)\s+[^)]*\)\s*$/i, "")
    .trim();
}

/** Compute granular fit label for a single measurement. Mirrors the
 *  backend deterministic engine's edge-based thresholds so the FIT
 *  DIRECTIVES the SDK ships to /tryon match what the user reads in the
 *  table:
 *    - "good"        : in chart range OR within ½" / 1.27 cm of nearest edge
 *    - "a-bit-X"     : ½"–1"  past the edge
 *    - "X"           : 1"–2"  past the edge
 *    - "too-X"       : > 2"   past the edge
 *  Where X is tight (over upper edge) or loose (under lower edge). */
export function computeFit(userValue: number, chartRange: string, unit?: "in" | "cm" | "mm"): GranularFit {
  const targetUnit = normalizeUnit(unit) || detectUnitFromText(chartRange) || "in";
  const chartUnit = detectUnitFromText(chartRange);
  const parsed = parseRange(chartRange);
  const rMin = chartUnit && chartUnit !== targetUnit ? convertValue(parsed.min, chartUnit, targetUnit) : parsed.min;
  const rMax = chartUnit && chartUnit !== targetUnit ? convertValue(parsed.max, chartUnit, targetUnit) : parsed.max;
  if (rMin === 0 && rMax === 0) return "good";

  const perfectTol = targetUnit === "cm" ? 1.27 : targetUnit === "mm" ? 12.7 : 0.5; // half inch
  const aBitTol    = targetUnit === "cm" ? 2.54 : targetUnit === "mm" ? 25.4 : 1.0; // one inch
  const tooFarTol  = targetUnit === "cm" ? 5.08 : targetUnit === "mm" ? 50.8 : 2.0; // two inches

  // In range OR within perfectTol of the nearest edge → good. Matches
  // backend deterministic.ts (sizing.deterministic.ts:fitTolerance +
  // perfectTol logic). Without this alignment, the FIT DIRECTIVES sent
  // to Gemini in the /tryon prompt say "a-bit-loose" while the SDK's
  // table reads "within range" for the same chart row.
  const inRange = userValue >= rMin && userValue <= rMax;
  const overEdge = inRange ? 0 : (userValue > rMax ? userValue - rMax : rMin - userValue);
  if (inRange || overEdge <= perfectTol) return "good";

  const isUnder = userValue < rMin;
  if (overEdge > tooFarTol) return isUnder ? "too-loose" : "too-tight";
  if (overEdge > aBitTol)   return isUnder ? "loose" : "tight";
  return isUnder ? "a-bit-loose" : "a-bit-tight";
}

/** Area name → pose-detect key mapping */
const AREA_TO_POSE_KEY: Record<string, keyof MeasurementLines> = {
  chest: "chest",
  bust: "chest",
  waist: "waist",
  hips: "hips",
  hip: "hips",
};

// Measurements that are NOT garment dimensions — they classify who the size
// fits (e.g. "fits heights 5'8"-6'1"") rather than a fabric measurement on
// the garment itself. Parsing "5'8"-6'1"" as numbers yields garbage (1-8),
// and telling Gemini "garment height = 1-8, body height = 70.1" confuses the
// model without ever driving a useful visual. Drop these from fitInfo.
const SKIP_AREAS_FOR_FIT = new Set<string>([
  "height",
  "altezza",
  "estatura",
]);

/**
 * Build FitAreaInfo[] from matchDetails + optional pose coordinates.
 * This is what gets sent to the backend so Gemini knows how to render each body region.
 */
export function buildFitInfo(
  matchDetails: Array<{ measurement: string; userValue: string; chartRange: string; fit: string; section?: string }>,
  poseLines?: MeasurementLines | null,
  unit?: "in" | "cm" | "mm",
): FitAreaInfo[] {
  return matchDetails
    .filter((m) => !SKIP_AREAS_FOR_FIT.has(m.measurement.toLowerCase().replace(/\s*\(.*?\)\s*/g, "").trim()))
    .map((m) => {
    const targetUnit = normalizeUnit(unit) || detectUnitFromText(m.userValue) || detectUnitFromText(m.chartRange) || "in";
    const userValueText = normalizeMeasurementValueText(m.measurement, m.userValue, targetUnit);
    const chartRangeText = normalizeRangeText(m.chartRange, targetUnit);
    const userNum = parseNum(userValueText);
    const fit = computeFit(userNum, chartRangeText, targetUnit);
    const info: FitAreaInfo = {
      area: m.measurement,
      section: m.section || undefined,
      fit,
      userValue: userNum || undefined,
      garmentRange: chartRangeText || undefined,
    };

    // Attach body coordinates if pose detection ran
    if (poseLines) {
      const poseKey = AREA_TO_POSE_KEY[m.measurement.toLowerCase()];
      if (poseKey && poseLines[poseKey]) {
        const line = poseLines[poseKey];
        info.y = Math.round(line.y * 1000) / 1000;
        info.x1 = Math.round(line.x1 * 1000) / 1000;
        info.x2 = Math.round(line.x2 * 1000) / 1000;
      }
    }

    return info;
  });
}

/**
 * Build silhouette-prompt context forwarded to the backend try-on Gemini
 * call. Returns only the selected fit context Gemini needs: user raw
 * measurements, recommended size, and the chosen size's measurement row.
 * The full product size guide is intentionally not forwarded to try-on.
 *
 * All fields optional — returns `undefined` when there's nothing useful
 * to add (e.g. accessory flow, no sizingResult).
 */
export function buildSilhouetteContext(
  sizingResult: {
    recommendedSize?: string;
    recommendedLength?: string | null;
    unit?: string;
    matchDetails?: Array<{ measurement: string; userValue: string; chartRange: string }>;
    /** Backend-formatted matched row text, e.g. "Chest (in) 40, Chest (cm) 102". Single-section. */
    matchedRowText?: string;
    sections?: Record<string, {
      recommendedSize?: string;
      matchDetails?: Array<{ measurement: string; userValue: string; chartRange: string }>;
      /** Backend-formatted matched row text for this section. */
      matchedRowText?: string;
    }>;
  } | null | undefined,
  sizeGuide: { headers?: string[]; rows?: string[][]; sections?: Record<string, { headers: string[]; rows: string[][] }> } | null | undefined,
  /** Optional override — when the user manually picks a different size in
   *  the result view and hits "Apply this size", we want Gemini to reason
   *  about THAT size, not the original recommendation. */
  selectedSizeOverride?: string,
  /** Active profile's height/weight, formatted for prompt display (e.g.
   *  `"5'6\""` and `"185 lbs"`). Passed straight to the backend so the
   *  prime directive can include the model's frame. */
  userHeight?: string,
  userWeight?: string,
):
  | { recommendedSize?: string; recommendedSizeMeasurements?: string; userMeasurementsText?: string; userHeight?: string; userWeight?: string }
  | undefined {
  if (!sizingResult && !sizeGuide && !userHeight && !userWeight) return undefined;

  const out: { recommendedSize?: string; recommendedSizeMeasurements?: string; userMeasurementsText?: string; userHeight?: string; userWeight?: string } = {};
  const promptUnit = normalizeUnit(sizingResult?.unit)
    || detectUnitFromText(sizingResult?.matchDetails?.[0]?.userValue)
    || detectUnitFromText(Object.values(sizingResult?.sections || {})[0]?.matchDetails?.[0]?.userValue)
    || "in";

  // Bare size (chart row label) — used for chart lookup. The combined
  // "size / Length N unit" string for the prompt is built afterward so
  // the lookup still hits "Missy 12" instead of "Missy 12 / Length 61 in".
  const baseSize = normalizePromptSizeLabel(selectedSizeOverride || sizingResult?.recommendedSize || "");

  if (userHeight) out.userHeight = userHeight;
  if (userWeight) out.userWeight = userWeight;

  // Find the recommended size's row in the chart so we can pass its
  // per-body-part measurements (Bust, Waist, Hips, Length...) to Gemini —
  // otherwise the prompt only carries the size label, which Gemini reads
  // as a vibe instead of an absolute target.
  let chartRowLength: string | null = null;
  let chartRowFit: string | null = null;
  // Prefer backend-provided matchedRowText (single-section) — it already
  // walked the chart and formatted the row. Falls back to local lookup if
  // the backend didn't ship it (older backends).
  if (sizingResult?.matchedRowText && !sizingResult?.sections) {
    out.recommendedSizeMeasurements = sizingResult.matchedRowText;
  } else if (baseSize && sizeGuide?.headers?.length && sizeGuide.rows?.length) {
    // Charts can have multiple label columns at the start (e.g. col 0 =
    // "Size (IT) 50", col 1 = "Standard L"). The recommendedSize might
    // match any of them, not just column 0. Scan the first 3 columns
    // for an exact case-insensitive match.
    const matchRow = sizeGuide.rows.find(r => {
      for (let i = 0; i < Math.min(3, r.length); i++) {
        if ((r[i] ?? "").toString().trim().toLowerCase() === baseSize.toLowerCase()) return true;
      }
      return false;
    });
    if (matchRow) {
      const hdrs = sizeGuide.headers;
      // Column 0 is the size label itself — skip it. The rest are
      // measurements (Bust, Waist, Hips, Length...) plus optional axis
      // columns (Fit, Silhouette, Category) that prefix the size label.
      const bits: string[] = [];
      for (let i = 1; i < hdrs.length; i++) {
        const v = matchRow[i];
        const vStr = v == null ? "" : String(v).trim();
        const h = hdrs[i] || "";
        // Axis columns get hoisted into the size label, not into measurements.
        if (chartRowFit == null && /^(fit|silhouette|category|body[\s_]?type)$/i.test(h) && vStr) {
          chartRowFit = vStr;
          continue;
        }
        if (vStr) bits.push(`${promptHeaderLabel(h) || h} ${normalizeRangeText(vStr, promptUnit, h)}`);
        // Capture the first Length / Inseam column value as a fallback for
        // the "/ Length N" suffix below — many flat charts (dresses, skirts)
        // expose length as a measurement column instead of a separate axis,
        // so sizingResult.recommendedLength is null for them.
        if (chartRowLength == null && /^length\b|inseam/i.test(h) && vStr) {
          chartRowLength = vStr;
        }
      }
      if (bits.length) out.recommendedSizeMeasurements = bits.join(", ");
    }
  }

  // Build the prompt label: "<Fit> <size> / Length <N> <unit>"
  // ("Missy 12 / Length 61 in" when Fit + Length are both present).
  if (baseSize) {
    const prefixed = chartRowFit ? `${chartRowFit} ${baseSize}` : baseSize;
    const length = sizingResult?.recommendedLength || chartRowLength;
    if (length) {
      const lenStr = String(length).trim();
      const unit = (sizingResult?.unit || "in").toString();
      const hasUnit = /[a-z]/i.test(lenStr);
      out.recommendedSize = `${prefixed} / Length ${lenStr}${hasUnit ? "" : ` ${unit}`}`;
    } else {
      out.recommendedSize = prefixed;
    }
  }

  // ── Multi-section override (suits, multi-piece outfits) ──
  // When sizingResult has sections (Jacket Size, Pant Length, Vest Size, …),
  // build a composite label AND a composite measurement string so Gemini sees
  // EACH section's chosen size and that size's chart-row dimensions — not
  // just the top-level recommendedSize (which is the last section processed).
  const sectionEntries = sizingResult?.sections ? Object.entries(sizingResult.sections) : [];
  if (sectionEntries.length > 0 && !selectedSizeOverride) {
    const labelParts: string[] = [];
    const measurementParts: string[] = [];
    for (const [secName, secResult] of sectionEntries) {
      const secSize = normalizePromptSizeLabel(secResult?.recommendedSize ?? "");
      if (!secSize) continue;
      // Trim trailing "Size" / "Length" qualifier on the section name for the
      // human-readable label ("Jacket Size" → "Jacket", "Pant Length" → "Pant Length").
      const cleanSec = /\bsize\s*$/i.test(secName) ? secName.replace(/\s*\bsize\s*$/i, "").trim() : secName;
      labelParts.push(`${cleanSec} ${secSize}`);
      // Prefer backend-formatted matchedRowText. Falls back to local chart
      // lookup if backend didn't ship it.
      if (secResult?.matchedRowText) {
        measurementParts.push(`${cleanSec} ${secSize}: ${secResult.matchedRowText}`);
        continue;
      }
      const secChart = sizeGuide?.sections?.[secName];
      if (secChart && secChart.headers?.length && secChart.rows?.length) {
        const row = secChart.rows.find(r => {
          for (let i = 0; i < Math.min(3, r.length); i++) {
            if ((r[i] ?? "").toString().trim().toLowerCase() === secSize.toLowerCase()) return true;
          }
          return false;
        });
        if (row) {
          const bits: string[] = [];
          for (let i = 0; i < secChart.headers.length; i++) {
            const v = row[i];
            const vStr = v == null ? "" : String(v).trim();
            const h = (secChart.headers[i] || "").trim();
            const baseHeader = h.replace(/\s*\(.*?\)\s*/g, "").trim();
            if (vStr && !/^(size|standard|country|fit|silhouette|category|body[\s_]?type|eu|uk|us|it|jp|cn|kr|ru|br|au)$/i.test(baseHeader)) {
              bits.push(`${promptHeaderLabel(h) || h} ${normalizeRangeText(vStr, promptUnit, h)}`);
            }
          }
          if (bits.length) measurementParts.push(`${cleanSec} ${secSize}: ${bits.join(", ")}`);
        }
      }
    }
    if (labelParts.length) out.recommendedSize = labelParts.join(", ");
    if (measurementParts.length) out.recommendedSizeMeasurements = measurementParts.join(" | ");
  }

  // Flatten matchDetails across the root + all sections. These are the exact
  // rows the deterministic matcher chose, so they are the only chart values
  // Gemini needs for try-on.
  const seenUserMeasurements = new Set<string>();
  const seenSelectedMeasurements = new Set<string>();
  const userLines: string[] = [];
  const selectedMeasurementLines: string[] = [];
  const push = (md?: Array<{ measurement: string; userValue: string; chartRange: string }>, sectionName?: string) => {
    if (!md) return;
    for (const m of md) {
      const normalizedMeasurement = m.measurement.toLowerCase().replace(/\s*\(.*?\)\s*/g, "").trim();
      if (SKIP_AREAS_FOR_FIT.has(normalizedMeasurement)) continue;
      const selectedKey = sectionName ? `${sectionName.toLowerCase()}:${normalizedMeasurement}` : normalizedMeasurement;
      if (m.userValue && !seenUserMeasurements.has(normalizedMeasurement)) {
        seenUserMeasurements.add(normalizedMeasurement);
        userLines.push(`${m.measurement} ${normalizeMeasurementValueText(m.measurement, m.userValue, promptUnit)}`);
      }
      if (m.chartRange && !seenSelectedMeasurements.has(selectedKey)) {
        seenSelectedMeasurements.add(selectedKey);
        const prefix = sectionName ? `${sectionName} ${m.measurement}` : m.measurement;
        selectedMeasurementLines.push(`${prefix} ${normalizeRangeText(m.chartRange, promptUnit)}`);
      }
    }
  };
  push(sizingResult?.matchDetails);
  if (sizingResult?.sections) {
    for (const [secName, sec] of Object.entries(sizingResult.sections)) push(sec.matchDetails, secName);
  }
  if (userLines.length) out.userMeasurementsText = userLines.join(", ");
  if (selectedMeasurementLines.length) out.recommendedSizeMeasurements = selectedMeasurementLines.join(", ");

  return Object.keys(out).length ? out : undefined;
}
